import React, { useEffect, useState } from "react";
import Logger from "../../../services/Logger";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Item from "../../components/Item";
import IngestService from "./IngestService";
import ConfirmDialog from "../../components/ConfirmDialog";

const AgentNasaApod = ({ setting, onRefresh }) => {
    const [nasaDate, setNasaDate] = useState(dayjs()); // Default to today
    const [nasaIngesting, setNasaIngesting] = useState(false);
    const [nasaError, setNasaError] = useState(null);
    const [nasaSuccess, setNasaSuccess] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Update NASA date when setting is loaded
    useEffect(() => {
        if (setting?.value) {
            // Parse the date and add 1 day
            const lastIngestedDate = dayjs(setting.value);
            if (lastIngestedDate.isValid()) {
                setNasaDate(lastIngestedDate.add(1, 'day'));
            }
        }
    }, [setting]);

    const handleNasaIngest = () => {
        if (!nasaDate || !nasaDate.isValid()) {
            setNasaError("Please select a valid date");
            return;
        }

        setShowConfirmDialog(true);
    };

    const confirmNasaIngest = async () => {
        try {
            setNasaIngesting(true);
            setNasaError(null);
            setNasaSuccess(null);

            // Format date as YYYY-MM-DD
            const dateString = nasaDate.format('YYYY-MM-DD');
            const result = await IngestService.ingestNasaApod(dateString);
            
            if (result.success) {
                setNasaSuccess("NASA APOD puzzle has been processed successfully");
                
                // Refresh all settings after ingest
                if (onRefresh) {
                    await onRefresh();
                }
            } else {
                setNasaError(result.error);
            }
        } catch (err) {
            Logger.error("Error ingesting NASA APOD:", err);
            setNasaError(err.message || "Failed to ingest NASA APOD");
        } finally {
            setNasaIngesting(false);
        }
    };

    return (
        <Item sx={{ height: '100%', width: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                        NASA APOD
                    </Typography>
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                Last Ingested Date:
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 1 }}>
                                {setting?.value || ""}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Date"
                                    value={nasaDate}
                                    onChange={(newValue) => setNasaDate(newValue)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNasaIngest}
                                disabled={nasaIngesting}
                                sx={{ minWidth: 140 }}
                            >
                                {nasaIngesting ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Progressing...
                                    </>
                                ) : (
                                    "Execute"
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                    {nasaSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {nasaSuccess}
                        </Alert>
                    )}
                    {nasaError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {nasaError}
                        </Alert>
                    )}
                    <Alert severity="info">
                        Process NASA Astronomy Picture of the Day (APOD). Scheduled: 11:00 AM every day
                    </Alert>
                </CardContent>
            </Card>

            <ConfirmDialog
                open={showConfirmDialog}
                setOpen={setShowConfirmDialog}
                title="Confirm NASA APOD Ingest"
                message={`Are you sure you want to process NASA APOD for date ${nasaDate.format('YYYY-MM-DD')}?`}
                callback={confirmNasaIngest}
            />
        </Item>
    );
};

export default AgentNasaApod;
